import React, {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react"
import { useForm } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"

import Checkbox from "@material-ui/core/Checkbox"
import FormControl from "@material-ui/core/FormControl"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import FormGroup from "@material-ui/core/FormGroup"
import Grid from "@material-ui/core/Grid"
import Typography from "@material-ui/core/Typography"
import makeStyles from "@material-ui/core/styles/makeStyles"

import ButtonWithProgress from "../../components/ButtonWIthProgress"
import StyledLink from "../../components/StyledLink"
import { ValidatableFormEmailField } from "../../components/textInput/FormEmailField"
import { ValidatableFormPasswordField } from "../../components/textInput/FormPasswordField"
import { AuthContext } from "../../context/auth"
import useValidator, { message } from "../../hooks/useValidator"
import cognitoMessages, { CognitoError } from "../../messages/cognitoError"
import ErrorHint from "./components/ErrorHint"
import FadeInGrid from "./components/FadeInGrid"
import { MAX_INPUT_LENGTH } from "../../constants"

const messages = defineMessages({
    email: {
        id: "login.panel.email",
        defaultMessage: "Email Address",
    },
    placeholder: {
        id: "password.field.placeholder",
        defaultMessage: "Password",
    },
    remember: {
        id: "login.panel.remember.device",
        defaultMessage: "Remember this device",
    },
    forgot: {
        id: "login.panel.forgot.password",
        defaultMessage: "Forgot Password?",
    },
    login: {
        id: "login",
        defaultMessage: "Log In",
    },
    signUpText: {
        id: "login.panel.hint.signUp.text",
        defaultMessage: "Don't have an account?",
    },
    signUpLink: {
        id: "login.panel.hint.signUp.link",
        defaultMessage: "Sign Up",
    },
    password: {
        id: "login.panel.input.placeholder.password",
        defaultMessage: "Password",
    },
    resendConfirmText: {
        id: "login.panel.hint.resend.confirm.text",
        defaultMessage: "Didn't get the confirm mail?",
    },
    resendConfirmLink: {
        id: "login.panel.hint.resend.confirm.link",
        defaultMessage: "Resend mail",
    },
})

export const useStyles = makeStyles({
    error: {
        marginBottom: "10px",
    },
    errWrapper: {
        margin: "0px 0 18px",
        position: "relative",
        minHeight: "46px",
    },
    formContorl: {
        margin: "16px 0 10px",
    },
    footerWrapper: {
        marginTop: "51px",
        "& > div:not(:first-child)": {
            marginTop: "12px",
        },
    },
    text: {
        color: "#949495",
        display: "flex",
        gap: 4,
    },
    textButton: {
        textTransform: "initial",
    },
    checkbox: {
        marginTop: "26px",
    },
    hint: {
        lineHeight: "28px",
    },
})

export default function LoginPanel() {
    const classes = useStyles()
    const intl = useIntl()
    const { doLogin, loginError, loading, rememberDevice, setRememberDevice } =
        useContext(AuthContext)

    const validator = useValidator()
    const { control, handleSubmit, clearErrors, setError } = useForm({
        reValidateMode: "onSubmit",
    })

    const [errMsg, setErrMsg] = useState("")

    const onRememberDeviceChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setRememberDevice(e.target.checked),
        [setRememberDevice],
    )

    const onChange = useCallback(() => setErrMsg(""), [])

    const onSubmit = useCallback(
        (data) => {
            const { email, password } = data
            doLogin(email.toLowerCase(), password)
        },
        [doLogin],
    )

    const [showResendConfirm, setShowResendConfirm] = useState(false)

    useEffect(() => {
        const code = loginError?.code
        if (code) {
            switch (code) {
                case CognitoError.UserNotFoundException:
                    setErrMsg(
                        intl.formatMessage(
                            cognitoMessages[
                                CognitoError.NotAuthorizedException
                            ],
                        ),
                    )
                    break
                case CognitoError.NotAuthorizedException:
                    setErrMsg(
                        intl.formatMessage(
                            cognitoMessages[
                                CognitoError.NotAuthorizedException
                            ],
                        ),
                    )
                    break
                case CognitoError.UserNotConfirmedException:
                    setShowResendConfirm(true)
                    setErrMsg(
                        intl.formatMessage(
                            cognitoMessages[
                                CognitoError.UserNotConfirmedException
                            ],
                        ),
                    )
                    break
                case CognitoError.UsernameExistsException:
                    setErrMsg(
                        intl.formatMessage(
                            cognitoMessages[
                                CognitoError.UsernameExistsException
                            ],
                        ),
                    )
                    break
                default:
                    console.log(`loginError.message:${loginError.message}`)
                    setErrMsg(loginError.message)
            }
        } else {
            const message: any = loginError.message || loginError
            if (typeof message === "string") {
                setErrMsg(message)
            }
        }
    }, [loginError, intl])
    function checkValidateLength(
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        name: "email" | "password",
    ) {
        if (e.target.value.length >= MAX_INPUT_LENGTH) {
            setError(name, {
                type: "manual",
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            })
        }
    }
    return (
        <FadeInGrid>
            <Grid item xs>
                <form onSubmit={handleSubmit(onSubmit)} onChange={onChange}>
                    <Grid item>
                        <FormControl className={classes.formContorl} fullWidth>
                            <ValidatableFormEmailField
                                control={control}
                                clearErrors={clearErrors}
                                name="email"
                                onChange={(e) =>
                                    checkValidateLength(e, "email")
                                }
                                inputProps={{
                                    maxLength: MAX_INPUT_LENGTH,
                                }}
                                rules={validator.email}
                            />
                        </FormControl>
                        <FormControl className={classes.formContorl} fullWidth>
                            <ValidatableFormPasswordField
                                control={control}
                                clearErrors={clearErrors}
                                name="password"
                                onChange={(e) =>
                                    checkValidateLength(e, "password")
                                }
                                inputProps={{
                                    maxLength: MAX_INPUT_LENGTH,
                                }}
                                rules={validator.password}
                                large={true}
                                label={intl.formatMessage(messages.password)}
                            />
                        </FormControl>
                        <FormGroup row>
                            <FormControlLabel
                                className={`${classes.text} ${classes.checkbox}`}
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={!!rememberDevice}
                                        onChange={onRememberDeviceChange}
                                    />
                                }
                                label={intl.formatMessage(messages.remember)}
                            />
                        </FormGroup>
                    </Grid>
                    <Grid className={classes.errWrapper}>
                        {!!errMsg && <ErrorHint errMsg={errMsg} />}
                    </Grid>
                    <Grid item>
                        <ButtonWithProgress
                            type="submit"
                            loading={!!loading}
                            buttonText={intl.formatMessage(messages.login)}
                        />
                    </Grid>
                </form>
            </Grid>
            <Grid
                className={classes.footerWrapper}
                container
                justifyContent="flex-start"
                direction="column"
            >
                <Grid item>
                    <Typography
                        variant="button"
                        display="block"
                        className={classes.textButton}
                    >
                        <StyledLink underline="none" link="/l/forgot-password">
                            {intl.formatMessage(messages.forgot)}
                        </StyledLink>
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography
                        className={`${classes.text} ${classes.textButton}`}
                        variant="button"
                        display="block"
                    >
                        {intl.formatMessage(messages.signUpText)}{" "}
                        <StyledLink underline="none" link="/l/signup">
                            {intl.formatMessage(messages.signUpLink)}
                        </StyledLink>
                    </Typography>
                </Grid>
                {showResendConfirm && (
                    <Grid item>
                        <Typography
                            className={`${classes.text} ${classes.textButton}`}
                            variant="button"
                            display="block"
                        >
                            {intl.formatMessage(messages.resendConfirmText)}{" "}
                            <StyledLink
                                underline="none"
                                link="/l/resend-confirmation"
                            >
                                {intl.formatMessage(messages.resendConfirmLink)}
                            </StyledLink>
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </FadeInGrid>
    )
}
